
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Calendar, Clock, Repeat } from 'lucide-react';

interface NotificationReminderSettingsProps {
  reminderDaysLong: number;
  reminderDaysMedium: number;
  reminderDaysShort: number;
  reminderDaysCritical: number;
  sendHour: number;
  onReminderDaysLongChange: (value: number) => void;
  onReminderDaysMediumChange: (value: number) => void;
  onReminderDaysShortChange: (value: number) => void;
  onReminderDaysCriticalChange: (value: number) => void;
  onHourChange: (value: number) => void;
}

const NotificationReminderSettings = ({
  reminderDaysLong,
  reminderDaysMedium,
  reminderDaysShort,
  reminderDaysCritical,
  sendHour,
  onReminderDaysLongChange,
  onReminderDaysMediumChange,
  onReminderDaysShortChange,
  onReminderDaysCriticalChange,
  onHourChange
}: NotificationReminderSettingsProps) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium flex items-center gap-2">
        <Calendar className="h-5 w-5" />
        Configuration des rappels
      </h3>
      
      {/* Rappel long terme */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Rappel long terme (en jours)
        </Label>
        <div className="space-y-2">
          <Input
            type="number"
            min="1"
            max="300"
            value={reminderDaysLong}
            onChange={(e) => {
              const value = parseInt(e.target.value) || 1;
              if (value >= 1 && value <= 300) {
                onReminderDaysLongChange(value);
              }
            }}
            placeholder="Saisissez entre 1 et 300 jours"
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            Configuration libre de 1 à 300 jours avant l'expiration
          </p>
        </div>
      </div>

      {/* Rappel moyen terme */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Rappel moyen terme (en jours)
        </Label>
        <Select
          value={reminderDaysMedium.toString()}
          onValueChange={(value) => onReminderDaysMediumChange(parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="45">45 jours avant expiration</SelectItem>
            <SelectItem value="30">30 jours avant expiration</SelectItem>
            <SelectItem value="21">21 jours avant expiration</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Rappel court terme */}
      <div className="space-y-3">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Repeat className="h-4 w-4" />
          Rappel court terme répété (en jours)
        </Label>
        <Select
          value={reminderDaysShort.toString()}
          onValueChange={(value) => onReminderDaysShortChange(parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="15">15 jours avant expiration</SelectItem>
            <SelectItem value="10">10 jours avant expiration</SelectItem>
            <SelectItem value="7">7 jours avant expiration</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Des notifications seront envoyées quotidiennement à partir de cette période jusqu'à la date critique
        </p>
      </div>

      {/* Rappel critique */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Rappel critique (en jours)
        </Label>
        <Select
          value={reminderDaysCritical.toString()}
          onValueChange={(value) => onReminderDaysCriticalChange(parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5">5 jours avant expiration</SelectItem>
            <SelectItem value="3">3 jours avant expiration</SelectItem>
            <SelectItem value="1">1 jour avant expiration</SelectItem>
            <SelectItem value="0">Le jour même</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Heure d'envoi */}
      <div className="space-y-3">
        <Label className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Heure d'envoi
        </Label>
        <Select
          value={sendHour.toString()}
          onValueChange={(value) => onHourChange(parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner l'heure" />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 24 }, (_, i) => (
              <SelectItem key={i} value={i.toString()}>
                {i.toString().padStart(2, '0')}:00
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default NotificationReminderSettings;
